// Page « Circuit de validation » : l'écran du circuit dans le cadre de l'application, et l'espace réduit d'une
// personne qui n'a qu'un rôle de circuit (directeur financier, achats, direction…) : elle ne voit aucun écran de la
// carte (CV-S11), seulement ses décisions, ses alertes, sa délégation et ses clés.
import { useQuery } from '@tanstack/react-query';
import type { ClientEkera } from '../api/client';
import type { Moi, Sante } from '../api/types';
import type { FournisseurIdentite } from '../auth/identite';
import { ContexteEkera, useEkera, type Contexte } from '../contexte';
import { EcranCles } from './Cles';
import { naviguer, type Route } from '../routeur';
import { EcranCircuits, type OngletCircuit } from './Circuits';

const ONGLETS: OngletCircuit[] = ['a-decider', 'suivi', 'alertes', 'delegation', 'configuration'];

function ongletDe(params: URLSearchParams): OngletCircuit {
  const o = params.get('onglet');
  return (ONGLETS as string[]).includes(o ?? '') ? (o as OngletCircuit) : 'a-decider';
}

function aller(o: { onglet?: OngletCircuit; instance?: string | null }) {
  if (o.instance) window.location.hash = `#/circuits/${o.instance}`;
  else naviguer('circuits', { onglet: o.onglet && o.onglet !== 'a-decider' ? o.onglet : undefined });
}

export function PageCircuits({ params }: { params: URLSearchParams }) {
  const { client, moi, administrateur } = useEkera();
  const cm = useQuery({ queryKey: ['circuits', 'moi'], queryFn: () => client.circuitsMoi(), retry: false });
  const adminCircuit = administrateur || (cm.data?.roles_circuit ?? []).includes('administration_circuit');
  return (
    <EcranCircuits
      client={client}
      sujet={moi.sujet ?? ''}
      emetteur={moi.emetteur}
      administrateur={adminCircuit}
      lecteurCarte={moi.roles.includes('lecture')}
      onglet={ongletDe(params)}
      instance={params.get('instance')}
      aller={aller}
    />
  );
}

/** L'espace d'une personne qui n'a qu'un rôle de circuit : ni carte, ni constats, ni documents. */
export function EspaceCircuit({
  client,
  identite,
  route,
  demonstration = false,
}: {
  client: ClientEkera;
  identite: FournisseurIdentite;
  route: Route;
  /** Démonstration déclarée par /config.json (W3). */
  demonstration?: boolean;
}) {
  const cm = useQuery({ queryKey: ['circuits', 'moi'], queryFn: () => client.circuitsMoi(), retry: false });
  const u = identite.utilisateur();
  const params = route.page === 'circuits' ? route.params : new URLSearchParams();
  return (
    <main className="pile" style={{ padding: 32, maxWidth: 1100 }} id="contenu">
      <header className="ligne" style={{ justifyContent: 'space-between' }}>
        <h1>Circuit de validation</h1>
        <span>
          {u?.nom ?? u?.sujet} ·{' '}
          <button type="button" className="lien-btn" onClick={() => void identite.deconnecter()}>
            Se déconnecter
          </button>
        </span>
      </header>
      <p className="note" data-testid="espace-circuit">
        Votre compte tient un rôle dans le circuit de validation de votre entreprise, sans accès à la carte : vous voyez ici
        les objets sur lesquels vous êtes sollicité, tant qu’ils sont ouverts.
      </p>
      <p className="ligne">
        <a href="#/circuits" aria-current={route.page !== 'cles' ? 'page' : undefined}>Circuit de validation</a> ·{' '}
        <a href="#/cles" aria-current={route.page === 'cles' ? 'page' : undefined}>Mes clés de sécurité</a>
      </p>
      {route.page === 'cles' && cm.data ? (
        <ContexteEkera.Provider value={contexteReduit(client, identite, cm.data, demonstration)}>
          <EcranCles />
        </ContexteEkera.Provider>
      ) : (
        <EcranCircuits
          client={client}
          sujet={u?.sujet ?? ''}
          administrateur={(cm.data?.roles_circuit ?? []).includes('administration_circuit')}
          lecteurCarte={false}
          onglet={ongletDe(params)}
          instance={params.get('instance')}
          aller={aller}
        />
      )}
    </main>
  );
}

/**
 * Le contexte de l'écran « Mes clés » pour une personne sans rôle d'accès : aucun rôle, aucune carte ; l'état des clés
 * vient de ``/v0/circuits/moi``. Seuls l'enrôlement, la liste et la révocation de ses propres clés lui sont ouverts.
 */
export function contexteReduit(client: ClientEkera, identite: FournisseurIdentite, cm: { webauthn?: unknown }, demonstration: boolean): Contexte {
  const u = identite.utilisateur();
  const moi = {
    client: '',
    sujet: u?.sujet ?? '',
    nom: u?.nom ?? null,
    emetteur: '',
    roles: [],
    compte_service: false,
    authentification: 'oidc',
    anonyme: false,
    validation_ecritures: false,
    validation_ecritures_motif: null,
    webauthn: (cm.webauthn ?? null) as Moi['webauthn'],
    membre_circuit: true,
  } as unknown as Moi;
  return {
    client,
    identite,
    cid: '',
    moi,
    sante: {} as Sante,
    ecritureAutorisee: false,
    administrateur: false,
    peutPartager: false,
    motifLectureSeule: 'Votre compte ne tient qu’un rôle dans le circuit de validation.',
    demonstration,
  };
}
